/**
 * WBSPanel — standalone WBS tree browser.
 *
 * STATUS: Available but not currently mounted in any route.
 *
 * The WBS tree visible in the active /scheduling view is rendered inline
 * by UnifiedScheduleView.renderNode (see UnifiedScheduleView.js around the
 * "Primavera-style tree node render" comment). That inline tree is
 * interleaved with activity rows in a unified grid sharing column widths
 * and scroll sync — it is structurally different from this component.
 *
 * This component is a *standalone* tree: focused, sidebar-style, no activity
 * interleaving. It exists so a future route (e.g., a dedicated WBS-management
 * screen) can mount it without re-implementing the tree from scratch.
 *
 * IT IS NOT DEAD CODE. The exported member of components/scheduling/index.js
 * is intentional. The delete button, AlertDialog, and handler wired below
 * are kept in sync with the equivalent flows in UnifiedScheduleView so the
 * two views stay behaviorally compatible — when this panel is eventually
 * mounted, delete will work the same way the user expects from the unified
 * view. Maintain both in parallel; do not let them drift.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useProject } from '../../contexts/ProjectContext';
import { Action } from '../../lib/auth';
import api from '../../lib/api';
import { ScrollArea } from '../ui/scroll-area';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FolderOpen,
  Search,
  Loader2,
  Plus,
  FolderPlus,
  FileText,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import wbsLogo from '../../assets/logos/wbs.png';

// Recursive count of descendant WBS nodes (excludes the node itself).
// Kept in sync with the same helper in UnifiedScheduleView.js — both surfaces
// share the same delete-confirmation copy.
function countDescendantWbs(node) {
  if (!node?.children?.length) return 0;
  return node.children.reduce((sum, c) => sum + 1 + countDescendantWbs(c), 0);
}

// Sum of activity_count across a node and all descendants.
function sumActivities(node) {
  if (!node) return 0;
  const own = node.activity_count || 0;
  const kids = node.children?.reduce((s, c) => s + sumActivities(c), 0) || 0;
  return own + kids;
}

const WBSPanel = ({ selectedWbsId, onSelectWbs, onWbsChange }) => {
  const { activeSchedule, can } = useProject();
  // WBS create/delete is structural work — gated by EDIT_STRUCTURE against the
  // caller's role on the active project (mirrors backend require_can on /wbs).
  // Use the PROJECT-bound can(): the GET /projects/{id} payload has no role
  // field, so gating via useAuth().can with the project object's role always
  // passed undefined and wrongly denied every member. See the source guard
  // no-activeProject-role-gate.test.js.
  const canEditStructure = can(Action.EDIT_STRUCTURE);
  const [wbsTree, setWbsTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedNodes, setExpandedNodes] = useState({});
  
  // Add WBS dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newWbs, setNewWbs] = useState({ code: '', name: '', description: '' });
  const [addAsChild, setAddAsChild] = useState(false);

  // Delete WBS state
  const [pendingDeleteNode, setPendingDeleteNode] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchWBSTree = useCallback(async () => {
    if (!activeSchedule?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/wbs/schedule/${activeSchedule.id}/tree`);
      setWbsTree(response.data);
      
      // Auto-expand all nodes initially for better visibility
      const initialExpanded = {};
      const expandAll = (nodes) => {
        nodes.forEach(node => {
          initialExpanded[node.id] = true;
          if (node.children) expandAll(node.children);
        });
      };
      expandAll(response.data);
      setExpandedNodes(initialExpanded);
    } catch (error) {
      console.error('Failed to fetch WBS tree:', error);
    } finally {
      setLoading(false);
    }
  }, [activeSchedule?.id]);

  useEffect(() => {
    fetchWBSTree();
  }, [fetchWBSTree]);

  const toggleExpand = (nodeId, e) => {
    e.stopPropagation();
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  };

  const handleSelectNode = (node) => {
    onSelectWbs(selectedWbsId === node.id ? null : node.id, node);
  };

  // Filter tree based on search
  const filterTree = useCallback((nodes, query) => {
    if (!query) return nodes;
    
    const lowerQuery = query.toLowerCase();
    
    const filterNode = (node) => {
      const matches = node.name.toLowerCase().includes(lowerQuery) || 
                     node.code.toLowerCase().includes(lowerQuery);
      
      const filteredChildren = node.children ? node.children.map(filterNode).filter(Boolean) : [];
      
      if (matches || filteredChildren.length > 0) {
        return { ...node, children: filteredChildren };
      }
      return null;
    };
    
    return nodes.map(filterNode).filter(Boolean);
  }, []);

  const filteredTree = useMemo(() => 
    filterTree(wbsTree, searchQuery), 
    [wbsTree, searchQuery, filterTree]
  );

  // Handle Add WBS
  const handleOpenAddDialog = (asChild = false) => {
    setAddAsChild(asChild);
    setNewWbs({ code: '', name: '', description: '' });
    setShowAddDialog(true);
  };

  const handleCreateWbs = async (e) => {
    e.preventDefault();
    if (!newWbs.code.trim() || !newWbs.name.trim()) {
      toast.error('Code and Name are required');
      return;
    }

    setIsCreating(true);
    try {
      const payload = {
        code: newWbs.code.trim(),
        name: newWbs.name.trim(),
        description: newWbs.description?.trim() || null,
        schedule_id: activeSchedule.id,
        parent_id: addAsChild && selectedWbsId ? selectedWbsId : null
      };

      await api.post('/wbs', payload);
      toast.success('WBS element created');
      setShowAddDialog(false);
      setNewWbs({ code: '', name: '', description: '' });
      
      // Refresh tree
      await fetchWBSTree();
      
      // Notify parent of change (for CPM recalculation)
      if (onWbsChange) onWbsChange();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to create WBS';
      toast.error(message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteWbs = async () => {
    if (!pendingDeleteNode) return;
    setIsDeleting(true);
    try {
      // Backend returns a WbsDeleteResponse cascade payload — this panel
      // doesn't render activities so we don't merge cascaded[], but we
      // surface the counts in the success toast for parity with the
      // unified-view experience.
      const { data } = await api.delete(`/wbs/${pendingDeleteNode.id}`);
      const wbsCount = data?.deleted_wbs_ids?.length ?? 1;
      const actCount = data?.deleted_activity_ids?.length ?? 0;
      const detail = actCount > 0
        ? ` (${wbsCount} element${wbsCount === 1 ? '' : 's'}, ${actCount} activit${actCount === 1 ? 'y' : 'ies'})`
        : '';
      toast.success(`"${pendingDeleteNode.name}" deleted${detail}`);
      setPendingDeleteNode(null);
      await fetchWBSTree();
      onWbsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete WBS element');
    } finally {
      setIsDeleting(false);
    }
  };

  // Primavera-style tree node render
  const renderNode = (node, level = 0) => {
    const hasChildren = node.children && node.children.length > 0;
    const isExpanded = expandedNodes[node.id];
    const isSelected = selectedWbsId === node.id;
    const hasActivities = node.activity_count > 0;
    
    return (
      <div key={node.id} className="select-none">
        {/* Node Row */}
        <div
          className={`
            group flex items-center h-7 border-l-2 transition-colors
            ${isSelected 
              ? 'bg-blue-50 border-l-blue-600' 
              : 'border-l-transparent hover:bg-slate-50 hover:border-l-slate-300'
            }
          `}
          style={{ paddingLeft: `${level * 20 + 4}px` }}
          onClick={() => handleSelectNode(node)}
          data-testid={`wbs-node-${node.id}`}
        >
          {/* Expand/Collapse Toggle */}
          <button
            onClick={(e) => hasChildren && toggleExpand(node.id, e)}
            className={`
              flex items-center justify-center w-5 h-5 rounded-sm mr-1 transition-colors
              ${hasChildren ? 'hover:bg-slate-200 cursor-pointer' : 'cursor-default'}
            `}
          >
            {hasChildren ? (
              isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-slate-500" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-slate-500" />
              )
            ) : (
              <span className="w-3.5" />
            )}
          </button>
          
          {/* Checkbox */}
          <div className="mr-2">
            <Checkbox
              checked={isSelected}
              className="h-3.5 w-3.5 rounded-sm border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
              onClick={(e) => e.stopPropagation()}
              onCheckedChange={() => handleSelectNode(node)}
            />
          </div>
          
          {/* Folder Icon */}
          <div className="mr-2 flex-shrink-0">
            {hasChildren ? (
              isExpanded ? (
                <FolderOpen className="h-4 w-4 text-amber-500" />
              ) : (
                <Folder className="h-4 w-4 text-amber-600" />
              )
            ) : hasActivities ? (
              <FileText className="h-4 w-4 text-slate-400" />
            ) : (
              <Folder className="h-4 w-4 text-slate-400" />
            )}
          </div>
          
          {/* WBS Code */}
          <span className={`
            text-[11px] font-mono mr-2 flex-shrink-0 px-1 py-0.5 rounded
            ${isSelected ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'}
          `}>
            {node.code}
          </span>
          
          {/* WBS Name */}
          <span className={`
            text-xs truncate flex-1
            ${isSelected ? 'text-blue-900 font-medium' : 'text-slate-700'}
          `}>
            {node.name}
          </span>
          
          {/* Activity Count Badge */}
          {hasActivities && (
            <span className={`
              text-[10px] px-1.5 py-0.5 rounded-full ml-auto mr-2 tabular-nums
              ${isSelected ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-500'}
            `}>
              {node.activity_count}
            </span>
          )}
          
          {/* Quick Add Child Button (on hover) */}
          {canEditStructure && (
            <button
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-slate-200 mr-1 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onSelectWbs(node.id, node);
                setTimeout(() => handleOpenAddDialog(true), 100);
              }}
              title="Add child element"
            >
              <FolderPlus className="h-3.5 w-3.5 text-slate-400" />
            </button>
          )}

          {/* Delete WBS Button (on hover, requires EDIT_STRUCTURE) */}
          {canEditStructure && (
            <button
              className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-red-100 mr-1 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                setPendingDeleteNode(node);
              }}
              title="Delete WBS element"
            >
              <Trash2 className="h-3.5 w-3.5 text-red-400" />
            </button>
          )}
        </div>
        
        {/* Children */}
        {hasChildren && isExpanded && (
          <div className="relative">
            {/* Vertical connector line */}
            <div 
              className="absolute left-0 top-0 bottom-0 border-l border-slate-200"
              style={{ marginLeft: `${level * 20 + 16}px` }}
            />
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Count total WBS nodes
  const countNodes = (nodes) => {
    let count = nodes.length;
    nodes.forEach(node => {
      if (node.children) count += countNodes(node.children);
    });
    return count;
  };

  const totalNodes = countNodes(wbsTree);
  const visibleNodes = countNodes(filteredTree);

  // Expand/Collapse All
  const handleExpandAll = () => {
    const allExpanded = {};
    const expandAll = (nodes) => {
      nodes.forEach(node => {
        allExpanded[node.id] = true;
        if (node.children) expandAll(node.children);
      });
    };
    expandAll(wbsTree);
    setExpandedNodes(allExpanded);
  };

  const handleCollapseAll = () => {
    setExpandedNodes({});
  };

  return (
    <div className="flex flex-col h-full bg-white" data-testid="wbs-panel">
      {/* Header - WBS Structure (part of Schedule) */}
      <div className="p-3 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Folder className="h-4 w-4 text-amber-600" />
            <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
              WBS Structure
            </h3>
          </div>
          {canEditStructure && (
            <div className="flex items-center gap-1">
              {selectedWbsId && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 hover:bg-slate-200"
                  onClick={() => handleOpenAddDialog(true)}
                  title="Add child WBS"
                >
                  <FolderPlus className="h-3.5 w-3.5 text-slate-500" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 hover:bg-slate-200"
                onClick={() => handleOpenAddDialog(false)}
                title="Add root WBS"
                data-testid="add-wbs-button"
              >
                <Plus className="h-3.5 w-3.5 text-slate-500" />
              </Button>
            </div>
          )}
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <Input
            placeholder="Filter WBS..."
            className="pl-8 h-7 text-xs bg-white border-slate-200"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="wbs-search-input"
          />
        </div>
        
        {/* Expand/Collapse Controls */}
        <div className="flex items-center gap-2 mt-2">
          <button 
            className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline"
            onClick={handleExpandAll}
          >
            Expand All
          </button>
          <span className="text-slate-300">|</span>
          <button 
            className="text-[10px] text-blue-600 hover:text-blue-700 hover:underline"
            onClick={handleCollapseAll}
          >
            Collapse All
          </button>
        </div>
      </div>
      
      {/* Tree Content */}
      <ScrollArea className="flex-1">
        <div className="py-1">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="text-center py-8 px-4">
              <img src={wbsLogo} alt="" className="h-10 w-10 mx-auto mb-3 opacity-30 object-contain" />
              <p className="text-sm font-medium text-slate-600 mb-1">
                {searchQuery ? 'No matching elements' : 'No WBS Structure'}
              </p>
              <p className="text-xs text-slate-400 mb-3">
                {searchQuery 
                  ? 'Try a different search term' 
                  : 'Create your first WBS element to organize activities'}
              </p>
              {canEditStructure && !searchQuery && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => handleOpenAddDialog(false)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add WBS Element
                </Button>
              )}
            </div>
          ) : (
            <div className="min-w-max">
              {filteredTree.map(node => renderNode(node))}
            </div>
          )}
        </div>
      </ScrollArea>
      
      {/* Footer */}
      <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <p className="text-[10px] text-slate-500">
          {searchQuery ? `${visibleNodes} of ${totalNodes}` : `${totalNodes}`} element{totalNodes !== 1 ? 's' : ''}
        </p>
        {selectedWbsId && (
          <button 
            className="text-[10px] text-blue-600 hover:underline"
            onClick={() => onSelectWbs(null, null)}
          >
            Clear Selection
          </button>
        )}
      </div>

      {/* Add WBS Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold flex items-center gap-2">
              <img src={wbsLogo} alt="" className="h-4 w-4 object-contain" />
              {addAsChild ? 'Add Child WBS' : 'Add WBS Element'}
            </DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              {addAsChild 
                ? 'Create a child element under the selected WBS' 
                : 'Create a new root WBS element in the hierarchy'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateWbs}>
            <div className="space-y-3 py-3">
              <div className="space-y-1.5">
                <Label htmlFor="wbs-code" className="text-xs font-medium">
                  WBS Code <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wbs-code"
                  placeholder="e.g., 1.0 or 1.1.1"
                  value={newWbs.code}
                  onChange={(e) => setNewWbs({ ...newWbs, code: e.target.value })}
                  className="h-8 text-sm font-mono"
                  data-testid="wbs-code-input"
                />
                <p className="text-[10px] text-slate-400">Use hierarchical numbering (e.g., 1.0, 1.1, 1.1.1)</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wbs-name" className="text-xs font-medium">
                  Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="wbs-name"
                  placeholder="WBS element name"
                  value={newWbs.name}
                  onChange={(e) => setNewWbs({ ...newWbs, name: e.target.value })}
                  className="h-8 text-sm"
                  data-testid="wbs-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="wbs-description" className="text-xs font-medium">
                  Description
                </Label>
                <Input
                  id="wbs-description"
                  placeholder="Optional description"
                  value={newWbs.description}
                  onChange={(e) => setNewWbs({ ...newWbs, description: e.target.value })}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDialog(false)}
                disabled={isCreating}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-slate-900 hover:bg-slate-800"
                disabled={isCreating}
                data-testid="create-wbs-submit"
              >
                {isCreating ? (
                  <>
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create WBS'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete WBS confirmation
          Guarded against close attempts while DELETE is in flight, same
          contract as the unified-view WBS delete dialog. */}
      <AlertDialog
        open={!!pendingDeleteNode}
        onOpenChange={(open) => {
          if (isDeleting) return;
          if (!open) setPendingDeleteNode(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete WBS element?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{pendingDeleteNode?.name}</strong> and everything below it will be permanently deleted
              {' — '}
              {countDescendantWbs(pendingDeleteNode)} sub-element{countDescendantWbs(pendingDeleteNode) === 1 ? '' : 's'}
              {' and '}
              {sumActivities(pendingDeleteNode)} activit{sumActivities(pendingDeleteNode) === 1 ? 'y' : 'ies'}.
              {' '}All dependency arrows touching these activities will be removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteWbs}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              {isDeleting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting…</>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WBSPanel;
